import React, { useState, useEffect, useRef } from "react";
import { Upload, Typography, message } from "antd";
import {
  PictureOutlined,
  EyeOutlined,
  DeleteOutlined,
  DownloadOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  LoadingOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import config from "../../../config";

const baseUrl = config.baseUrl;
const { Title } = Typography;

const allowedImages = /jpeg|jpg|png|heic|heif|webp|gif|tiff|bmp|svg/;

// ─── Estilos (mantén los mismos que tenías, incluido overlay de error) ──────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=Syne:wght@700;800&display=swap');

  .iup-card {
    font-family: 'DM Sans', sans-serif;
    border-radius: 14px;
    border: 2px solid #D0E0F2;
    overflow: hidden;
    box-shadow: 0 4px 24px rgba(92,92,224,0.12);
    background: #fff;
    margin-bottom: 24px;
  }

  .iup-card .iup-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 13px 20px;
    background: linear-gradient(135deg, #338BC7 0%, #338BC7 100%);
  }

  .iup-card .iup-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .iup-card .iup-icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: rgba(255,255,255,0.18);
    color: #fff;
    font-size: 15px;
    flex-shrink: 0;
  }

  .iup-card .iup-title {
    font-size: 12px !important;
    font-weight: 800 !important;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: #fff !important;
    margin: 0 !important;
    line-height: 1 !important;
  }

  .iup-card .iup-count {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 700;
    color: #fff;
    background: rgba(255,255,255,0.22);
    border: 1px solid rgba(255,255,255,0.35);
    border-radius: 100px;
    padding: 2px 10px;
  }

  .iup-card .iup-body {
    padding: 18px 20px 20px;
    background: #fff;
  }

  .iup-card .iup-zone {
    border: 1.5px dashed #c5c5f5;
    border-radius: 10px;
    background: #f5f5ff;
    padding: 16px 18px;
    transition: border-color 0.2s, background 0.2s;
  }

  .iup-card .iup-zone:hover {
    border-color: #338BC7;
    background: #ededff;
  }

  .iup-card .ant-upload-list { display: none !important; }

  .iup-card .iup-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    padding: 7px 16px;
    color: #338BC7;
    background: #fff;
    border: 1.5px solid #c5c5f5;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s, color 0.15s, transform 0.12s;
    white-space: nowrap;
  }
  .iup-card .iup-btn:hover {
    background: #338BC7;
    color: #fff;
    border-color: #338BC7;
    transform: translateY(-1px);
  }

  .iup-card .iup-hint {
    font-size: 11px;
    color: #aaa;
    margin-top: 7px;
  }

  .iup-card .iup-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 10px;
    margin-top: 16px;
  }

  .iup-card .iup-skeleton {
    aspect-ratio: 1;
    border-radius: 10px;
    background: linear-gradient(90deg, #e8eef4 25%, #d4e3ef 50%, #e8eef4 75%);
    background-size: 200% 100%;
    animation: iupShimmer 1.4s ease-in-out infinite;
  }

  @keyframes iupShimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  .iup-card .iup-loading-label {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
    font-size: 12px;
    color: #338BC7;
    font-weight: 500;
  }

  .iup-card .iup-loading-dots span {
    display: inline-block;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #338BC7;
    margin: 0 2px;
    animation: iupDot 1.2s ease-in-out infinite;
  }
  .iup-card .iup-loading-dots span:nth-child(2) { animation-delay: 0.2s; }
  .iup-card .iup-loading-dots span:nth-child(3) { animation-delay: 0.4s; }

  @keyframes iupDot {
    0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
    40%           { transform: scale(1);   opacity: 1;   }
  }

  .iup-card .iup-img-card {
    position: relative;
    z-index: 1;
    overflow: hidden;
    aspect-ratio: 1;
    border-radius: 10px;
    background: #ebebf5;
    cursor: pointer;
    transition: transform 0.18s, box-shadow 0.18s;
  }
  .iup-card .iup-img-card:hover {
    transform: translateY(-3px) scale(1.03);
    box-shadow: 0 6px 20px rgba(51,139,199,0.22);
  }
  .iup-card .iup-img-card img {
    width: 100%; height: 100%;
    object-fit: cover; display: block;
  }

  /* Overlay normal (Ver + Eliminar) */
  .iup-card .iup-overlay {
    position: absolute; inset: 0;
    background: rgba(15,15,40,0.5);
    display: flex; align-items: center; justify-content: center; gap: 8px;
    opacity: 0; transition: opacity 0.18s;
    backdrop-filter: blur(2px);
  }
  .iup-card .iup-img-card:hover .iup-overlay { opacity: 1; }

  .iup-card .iup-ov-btn {
    display: flex; align-items: center; justify-content: center;
    width: 34px; height: 34px; border-radius: 8px;
    border: none; cursor: pointer; font-size: 15px;
    transition: transform 0.12s;
  }
  .iup-card .iup-ov-btn:hover { transform: scale(1.1); }
  .iup-card .iup-ov-view { background: rgba(255,255,255,0.94); color: #338BC7; }
  .iup-card .iup-ov-del  { background: rgba(230,50,50,0.9);   color: #fff; }

/* Overlay de error (fondo oscuro y botones con íconos grandes) */
.iup-card .iup-overlay-error {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  opacity: 0;
  transition: opacity 0.25s ease;
  backdrop-filter: blur(4px);
  z-index: 5;
  pointer-events: none;
}
.iup-card .iup-img-card:hover .iup-overlay-error {
  opacity: 1;
  pointer-events: auto;
}

.iup-card .iup-overlay-error .iup-err-icon {
  font-size: 40px;
  color: #fff;
  text-shadow: 0 2px 12px rgba(0,0,0,0.6);
}

.iup-card .iup-overlay-error .iup-err-text {
  font-size: 14px;
  color: #fff;
  font-weight: 700;
  text-shadow: 0 1px 6px rgba(0,0,0,0.5);
}

.iup-card .iup-overlay-error .iup-err-actions {
  display: flex;
  gap: 16px;
  margin-top: 6px;
}

.iup-card .iup-overlay-error .iup-err-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  font-size: 22px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(2px);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.2);
}
.iup-card .iup-overlay-error .iup-err-btn:hover {
  transform: scale(1.15);
  background: rgba(255,255,255,0.3);
}
.iup-card .iup-overlay-error .iup-err-retry {
  background: rgba(51, 139, 199, 0.8);
  border-color: #338BC7;
}
.iup-card .iup-overlay-error .iup-err-retry:hover {
  background: #338BC7;
}
.iup-card .iup-overlay-error .iup-err-del {
  background: rgba(230, 50, 50, 0.8);
  border-color: #e63946;
}
.iup-card .iup-overlay-error .iup-err-del:hover {
  background: #e63946;
}


  /* Indicador de error dentro de la imagen (fondo) */
  .iup-card .iup-img-error-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f0f0f0;
    color: #999;
    font-size: 14px;
    font-weight: 600;
  }

  .iup-modal-bg {
    position: fixed; inset: 0;
    background: rgba(8,8,22,0.85);
    z-index: 9999;
    display: flex; align-items: center; justify-content: center;
    animation: iupFade 0.18s ease;
  }
  @keyframes iupFade { from { opacity:0; } to { opacity:1; } }

  .iup-modal-box {
    position: relative;
    max-width: 88vw; max-height: 88vh;
    border-radius: 16px; overflow: hidden;
    box-shadow: 0 24px 64px rgba(0,0,0,0.55);
    animation: iupScale 0.2s cubic-bezier(0.34,1.4,0.64,1);
  }
  @keyframes iupScale {
    from { transform: scale(0.86); opacity: 0; }
    to   { transform: scale(1);    opacity: 1; }
  }
  .iup-modal-box img { max-width: 88vw; max-height: 88vh; display: block; object-fit: contain; }

  .iup-modal-actions {
    position: absolute; bottom: 14px; right: 14px;
    display: flex; gap: 8px;
  }

  .iup-modal-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 7px 14px; border: none; border-radius: 8px;
    font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 600;
    cursor: pointer; transition: opacity 0.12s, transform 0.12s;
  }
  .iup-modal-btn:hover { opacity: 0.88; transform: translateY(-1px); }

  .iup-modal-dl    { background: #338BC7; color: #fff; }
  .iup-modal-close {
    background: rgba(255,255,255,0.15); color: #fff;
    backdrop-filter: blur(6px);
    border: 1px solid rgba(255,255,255,0.25) !important;
  }
`;
const ImagenUploader = ({
  titulo,
  onChange,
  value,
  onLoadingChange,
  onStatusChange,
  name,
}) => {
  const [imagenes, setImagenes] = useState([]);
  const [previewImg, setPreviewImg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Referencias para evitar bucles y llamadas innecesarias
  const prevValueRef = useRef(value);
  const prevStatusRef = useRef(null);

  // ─── Sincronización con el padre (sin conversión) ──────────────────────────
  useEffect(() => {
    const newValue = value || [];
    const newArray = Array.isArray(newValue) ? newValue : [newValue];

    // Evitar actualización si el valor no cambió realmente (comparación profunda)
    const prevValue = prevValueRef.current || [];
    const prevArray = Array.isArray(prevValue) ? prevValue : [prevValue];
    if (JSON.stringify(prevArray) === JSON.stringify(newArray)) {
      return;
    }
    prevValueRef.current = newArray;

    const nuevasImagenes = newArray.map((url, index) => {
      let finalUrl = url;
      // Si es una URL absoluta (http, https, data:image) la dejamos tal cual
      if (typeof url === "string") {
        if (
          url.startsWith("data:image") ||
          url.startsWith("http://") ||
          url.startsWith("https://")
        ) {
          // No modificar
        } else {
          // Ruta relativa → agregar baseUrl
          finalUrl = baseUrl + url;
        }
      }
      return {
        uid: `-${index}`,
        name: `imagen-${index}`,
        url: finalUrl,
        failed: false,
        retry: 0,
      };
    });

    setImagenes(nuevasImagenes);
  }, [value]);

  // ─── Notificar al padre sobre el estado de carga/error ─────────────────────
  useEffect(() => {
    const hasFailed = imagenes.some((img) => img.failed === true);
    const anyLoading = loading;

    let newStatus = null;
    if (hasFailed) {
      newStatus = false;
    } else if (!anyLoading) {
      newStatus = true;
    }
    // Si no hay cambio, no llamar a onStatusChange
    if (newStatus !== null && newStatus !== prevStatusRef.current) {
      prevStatusRef.current = newStatus;
      onStatusChange?.(name, newStatus);
    }
  }, [imagenes, loading, name, onStatusChange]);

  // ─── Handlers de subida y borrado ──────────────────────────────────────────
  const handleUploadChange = async (info) => {
    if (!info.fileList || info.fileList.length === 0) return;

    const existingUids = new Set(imagenes.map((img) => img.uid));
    const newFiles = info.fileList.filter((file) => !existingUids.has(file.uid));

    if (newFiles.length === 0) return;

    setLoading(true);
    onLoadingChange?.(name, true);
    setError(null);

    try {
      const nuevosBase64 = await Promise.all(
        newFiles.map(
          (file) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(file.originFileObj);
            })
        )
      );

      const nuevasImagenes = nuevosBase64.map((b64, idx) => ({
        uid: newFiles[idx].uid,
        name: newFiles[idx].name,
        url: b64, // base64 directo
        failed: false,
        retry: 0,
      }));

      const todas = [...imagenes, ...nuevasImagenes];
      setImagenes(todas);
      onChange(todas.map((img) => img.url));
      // El estado se actualizará en el useEffect de notificación
    } catch (err) {
      console.error("Error al leer archivos:", err);
      message.error("Error al procesar algunas imágenes.");
      setError("No se pudieron leer algunos archivos.");
      onStatusChange?.(name, false);
    } finally {
      setLoading(false);
      onLoadingChange?.(name, false);
    }
  };

  const beforeUpload = (file) => {
    const extension = file.name.split(".").pop().toLowerCase();
    const mimeType = file.type.toLowerCase();
    const valid = allowedImages.test(extension) || allowedImages.test(mimeType);
    if (!valid) {
      message.error(
        "Formato no permitido. Use: JPEG, JPG, PNG, HEIC, HEIF, WEBP, GIF, TIFF, BMP o SVG."
      );
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleDelete = (uid) => {
    const nuevas = imagenes.filter((img) => img.uid !== uid);
    setImagenes(nuevas);
    onChange(nuevas.map((img) => img.url));
    if (nuevas.length === 0) {
      setError(null);
      // El estado se actualizará en el useEffect de notificación
    }
  };

  const handleDownload = (img) => {
    const a = document.createElement("a");
    a.href = img.url;
    a.download = img.name || "imagen";
    a.click();
  };

  const handleRetry = (uid) => {
    setImagenes((prev) =>
      prev.map((img) =>
        img.uid === uid ? { ...img, failed: false, retry: img.retry + 1 } : img
      )
    );
  };

  const handleImageError = (uid) => {
    setImagenes((prev) =>
      prev.map((img) =>
        img.uid === uid ? { ...img, failed: true } : img
      )
    );
  };

  const getImageSrc = (img) => {
    if (img.url.startsWith("data:image")) return img.url;
    const separator = img.url.includes("?") ? "&" : "?";
    return `${img.url}${separator}retry=${img.retry}`;
  };

  const mostrarGrid = loading || imagenes.length > 0;

  return (
    <div className="iup-card">
      <style>{STYLES}</style>

      <div className="iup-header">
        <div className="iup-header-left">
          <span className="iup-icon-wrap">
            <PictureOutlined />
          </span>
          <Title level={4} className="iup-title">
            {titulo}
          </Title>
        </div>
        {loading ? (
          <span className="iup-count">
            <LoadingOutlined spin /> Subiendo...
          </span>
        ) : imagenes.length > 0 ? (
          <span className="iup-count">
            <PictureOutlined /> {imagenes.length} foto
            {imagenes.length !== 1 ? "s" : ""}
          </span>
        ) : null}
      </div>

      <div className="iup-body">
        <div className="iup-zone">
          <Upload
            multiple
            fileList={imagenes}
            beforeUpload={beforeUpload}
            onChange={handleUploadChange}
            action=""
          >
            <button className="iup-btn" type="button">
              <CloudUploadOutlined /> Cargar Imágenes
            </button>
          </Upload>
          <p className="iup-hint">
            JPEG · PNG · WEBP · HEIC · HEIF · GIF · TIFF · BMP · SVG · Múltiple
            selección permitida
          </p>
        </div>

        {error && (
          <div
            style={{
              marginTop: 12,
              padding: "8px 12px",
              background: "#fff2e8",
              border: "1px solid #ffa39e",
              borderRadius: 8,
            }}
          >
            <span style={{ color: "#cf1322" }}>⚠️ {error}</span>
          </div>
        )}

        {mostrarGrid && (
          <div className="iup-grid">
            {loading
              ? Array.from({ length: 3 }).map((_, i) => (
                  <div key={`sk-${i}`} className="iup-skeleton" />
                ))
              : imagenes.map((img) => {
                  const src = getImageSrc(img);
                  const isFailed = img.failed;

                  return (
                    <div key={img.uid} className="iup-img-card">
                      {!isFailed ? (
                        <img
                          src={src}
                          alt={img.name}
                          onError={() => handleImageError(img.uid)}
                        />
                      ) : (
                        <div className="iup-img-error-placeholder">
                          <span>⚠️ Error</span>
                        </div>
                      )}

                      {!isFailed && (
                        <div className="iup-overlay">
                          <button
                            className="iup-ov-btn iup-ov-view"
                            title="Ver"
                            type="button"
                            onClick={() => setPreviewImg(img)}
                          >
                            <EyeOutlined />
                          </button>
                          <button
                            className="iup-ov-btn iup-ov-del"
                            title="Eliminar"
                            type="button"
                            onClick={() => handleDelete(img.uid)}
                          >
                            <DeleteOutlined />
                          </button>
                        </div>
                      )}

                      {isFailed && (
                        <div className="iup-overlay-error">
                          <div className="iup-err-actions">
                            <button
                              className="iup-err-btn iup-err-retry"
                              type="button"
                              onClick={() => handleRetry(img.uid)}
                              title="Reintentar"
                            >
                              <ReloadOutlined />
                            </button>
                            <button
                              className="iup-err-btn iup-err-del"
                              type="button"
                              onClick={() => handleDelete(img.uid)}
                              title="Eliminar"
                            >
                              <DeleteOutlined />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
          </div>
        )}

        {loading && (
          <div className="iup-loading-label">
            Procesando imágenes
            <span className="iup-loading-dots">
              <span />
              <span />
              <span />
            </span>
          </div>
        )}
      </div>

      {previewImg && (
        <div className="iup-modal-bg" onClick={() => setPreviewImg(null)}>
          <div className="iup-modal-box" onClick={(e) => e.stopPropagation()}>
            <img src={previewImg.url} alt={previewImg.name} />
            <div className="iup-modal-actions">
              <button
                className="iup-modal-btn iup-modal-dl"
                type="button"
                onClick={() => handleDownload(previewImg)}
              >
                <DownloadOutlined /> Descargar
              </button>
              <button
                className="iup-modal-btn iup-modal-close"
                type="button"
                onClick={() => setPreviewImg(null)}
              >
                <CloseOutlined /> Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagenUploader;